<template>
    <v-dialog v-model="modal" :width="$conf.public.account.paymentSchedulerAutoPaymentMode ? 500 : 600" scrollable>
        <v-card :loading="isLoading">
            <v-card-title>
                {{
                    $conf.public.account.paymentSchedulerAutoPaymentMode
                        ? $t('add_payment')
                        : $t('add_payment_schedule')
                }}
            </v-card-title>
            <v-card-text>
                <div class="text-caption mt-n4" style="white-space: pre-line">
                    {{ $t('add_payment_schedule_description') }}
                </div>
                <v-row v-if="isShowTypeSwitcher" class="mt-2">
                    <v-col cols="12" md="6">
                        <v-select
                            v-model="invoiceType"
                            :items="invoiceTypes"
                            hide-details
                            @update:model-value="loadManualInvoiceIssueInfo"
                        />
                    </v-col>
                </v-row>
                <v-radio-group v-if="suppliers && suppliers.length > 1" v-model="supplierId">
                    <v-radio
                        v-for="supplier in suppliers"
                        :key="supplier.supplierId"
                        :label="`${supplier.supplierName}`"
                        :value="supplier.supplierId"
                    />
                </v-radio-group>
                <v-form
                    v-if="issueItemBySupplier"
                    ref="form"
                    v-model="valid"
                    :disabled="isLoading"
                    align="center"
                    class="pt-6"
                >
                    <v-row v-for="(price, i) in issueItemBySupplier.prices" :key="price.currency">
                        <v-col
                            :md="$conf.public.account.paymentSchedulerAutoPaymentMode ? 4 : 3"
                            :cols="$conf.public.account.paymentSchedulerAutoPaymentMode ? 8 : 10"
                        >
                            <div class="full-width full-height d-flex align-start justify-start flex-column">
                                <span class="text-caption mt-n2">{{ $t('balance_owing') }}</span>
                                <span v-if="price" class="text-h6 text-black mt-n1">
                                    {{ $filters.formatPrice({amount: price.totalAmount, currency: price.currency}) }}
                                </span>
                            </div>
                        </v-col>
                        <v-col
                            v-if="
                                isMobile &&
                                issueItemBySupplier.prices.length > 1 &&
                                $conf.public.account.paymentSchedulerAutoPaymentMode !== true
                            "
                            cols="2"
                        >
                            <v-btn icon="mdi-close" variant="text" @click="removePrice(i)" />
                        </v-col>
                        <v-col :md="4" cols="12">
                            <v-text-field
                                v-model="price.amount"
                                :prefix="price && price.currency"
                                hide-details
                                :label="`${$t('invoices.amount')}*`"
                                :rules="[
                                    requiredRule('invoices.amount_to_pay'),
                                    v => +v <= price.amount || `${$t('max_value_is')} ${price.amount}`,
                                ]"
                            />
                        </v-col>
                        <v-col v-if="!$conf.public.account.paymentSchedulerAutoPaymentMode" md="5" cols="12">
                            <date-picker
                                v-model="price.dueToPay"
                                :label="`${$t('invoices.due_date')}*`"
                                hide-details
                                :rules="[requiredRule('invoices.due_date')]"
                            />
                        </v-col>
                        <v-col
                            v-if="
                                !isMobile &&
                                issueItemBySupplier.prices.length > 1 &&
                                $conf.public.account.paymentSchedulerAutoPaymentMode !== true
                            "
                            cols="1"
                        >
                            <v-btn icon="mdi-close" class="ms-n3" @click="removePrice(i)" />
                        </v-col>
                        <v-col
                            v-if="$conf.public.account.paymentSchedulerAutoPaymentMode"
                            md="4"
                            cols="12"
                            class="d-flex align-center"
                        >
                            <v-spacer />
                            <v-btn
                                color="primary"
                                elevation="0"
                                block
                                :disabled="!price.amount"
                                :loading="loadingConfirm"
                                @click="silentPayPrice(i)"
                            >
                                <v-icon size="small" start> mdi-plus-circle-multiple-outline</v-icon>
                                <span class="text-caption">
                                    {{ $t('pay') }}
                                </span>
                            </v-btn>
                        </v-col>
                        <v-col v-if="price.prepaymentAmount" cols="12" class="py-0 text-start">
                            <span>
                                {{ $t('prepayment_amount_note') }}
                            </span>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col :cols="12">
                            <v-select
                                v-if="descriptionDropdownMode"
                                v-model="description"
                                hide-details
                                :label="`${$t('description')}`"
                                :items="descriptionItems"
                            >
                                <template v-slot:append>
                                    <v-btn
                                        v-if="descriptionItems && descriptionItems.length"
                                        class="mt-n1"
                                        color="primary"
                                        style="margin-left: 4px"
                                        icon="mdi-pencil"
                                        variant="text"
                                        @click="descriptionDropdownMode = !descriptionDropdownMode"
                                    />
                                    <div v-else class="px-5" />
                                </template>
                            </v-select>
                            <v-text-field
                                v-else
                                v-model="description"
                                variant="outlined"
                                hide-details
                                density="compact"
                                :label="`${$t('description')}`"
                            >
                                <template v-slot:append>
                                    <v-btn
                                        v-if="descriptionItems && descriptionItems.length"
                                        class="mt-n1"
                                        color="primary"
                                        style="margin-left: 4px"
                                        icon="mdi-select-place"
                                        variant="text"
                                        @click="descriptionDropdownMode = !descriptionDropdownMode"
                                    />
                                    <div v-else class="px-5" />
                                </template>
                            </v-text-field>
                        </v-col>
                    </v-row>
                </v-form>
                <v-row v-if="$conf.public.account.paymentSchedulerAutoInvoiceRemaining">
                    <v-col class="py-0" cols="12">
                        <v-checkbox
                            v-model="autoInvoiceRemainingBalance"
                            :disabled="isLoading"
                            hide-details
                            :label="$t('invoice_remaining_balance')"
                        />
                    </v-col>
                </v-row>
            </v-card-text>
            <v-card-actions class="justify-center py-5">
                <div v-if="$conf.public.account.paymentSchedulerAutoPaymentMode !== true" @click="confirm">
                    <v-btn variant="flat" color="primary" class="me-2" :disabled="!valid || isLoading">
                        {{ $t('confirm') }}
                    </v-btn>
                </div>
                <v-btn variant="flat" :disabled="isLoading" @click="modal = false">
                    {{ $t('cancel') }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script lang="ts">
    import {Component, Emit, mixins, Prop, VModel, Watch} from 'vue-facing-decorator'
    import DatePicker from '@/components/snippets/forms/DatePicker.vue'
    import {FormValidationRulesMixin} from '@/mixins/FormValidationRulesMixin.vue'

    @Component({
        components: {DatePicker},
    })
    export class OrderAddDepositModal extends mixins(FormValidationRulesMixin) {
        @VModel() modal
        @Prop() objectId

        valid = false
        loadingConfirm = false
        loadingInfo = false

        invoiceType = 'CUSTOMER'
        supplierId = null

        issueInfoItems = []
        autoInvoiceRemainingBalance = false

        description = null
        descriptionDropdownMode = false

        @Watch('modal')
        async onModalChange() {
            if (this.modal) {
                this.reset()
                await this.loadManualInvoiceIssueInfo()
            }
        }

        mounted() {
            if (this.descriptionItems && this.descriptionItems.length) {
                this.descriptionDropdownMode = true
            }
        }

        async loadManualInvoiceIssueInfo() {
            this.issueInfoItems = []
            this.supplierId = null
            this.loadingInfo = true
            this.$refs.form?.reset()

            try {
                const manualInvoiceIssueInfo = await this.$api.manualInvoiceIssueInfo.get({
                    orderId: this.objectId,
                    invoiceType: this.invoiceType,
                })
                const issueInfoItems = manualInvoiceIssueInfo.items

                issueInfoItems.forEach(item => {
                    item.prices.forEach(p => {
                        p.totalAmount = p.amount

                        if (this.invoiceType === 'SUPPLIER') {
                            if (p.dueToPay) {
                                p.dueToPay = p.dueToPay.split(' ')[0]
                            }
                            if (p.prepaymentAmount) {
                                p.amount = p.prepaymentAmount
                            }
                        } else if (this.invoiceType === 'CUSTOMER') {
                            const dueToPayFromInfo = manualInvoiceIssueInfo.dueToPay?.split(' ')[0]

                            p.dueToPay = this.$dateFns.isAfter(new Date(), new Date(dueToPayFromInfo))
                                ? this.$dateFns.format(new Date())
                                : dueToPayFromInfo
                        }
                    })
                })

                this.issueInfoItems = issueInfoItems
                this.supplierId = this.issueInfoItems?.[0]?.supplierId
                // eslint-disable-next-line no-empty
            } catch (e) {
            } finally {
                this.loadingInfo = false
            }
        }

        async confirm() {
            const tempSupplierId = this.supplierId

            await this.postInvoices()
            if (this.autoInvoiceRemainingBalance) {
                await this.loadManualInvoiceIssueInfo()
                if (tempSupplierId) {
                    this.supplierId = tempSupplierId
                }
                await this.postInvoices()
            }
            this.modal = false
        }

        @Emit()
        async postInvoices(autoPaymentMode = false) {
            let silentPayInvoiceId = null

            await this.$refs.form?.validate()

            if (this.valid) {
                this.loadingConfirm = true

                try {
                    const result = await Promise.all(
                        this.issueItemBySupplier.prices.map(price => {
                            const priceToSend = {...price}
                            const dueToPay = price.dueToPay

                            delete priceToSend.dueToPay
                            delete priceToSend.totalAmount
                            delete priceToSend.prepaymentAmount

                            if (priceToSend.amount !== 0) {
                                return this.$api.issueManualInvoice.post({
                                    orderId: this.objectId,
                                    invoiceType: this.invoiceType,
                                    supplierId: this.supplierId,
                                    prices: [priceToSend],
                                    ...(dueToPay && {dueToPay: `${dueToPay}T23:59`}),
                                    ...(this.description && {description: this.description}),
                                })
                            }
                        })
                    )

                    silentPayInvoiceId = result?.[0]?.invoices[0]?.invoiceId

                    if (!autoPaymentMode) {
                        this.$toast.success(this.$t('success_message.saved_successfully'))
                    }
                } catch (e) {
                    this.$toast.error(this.$t('error_message.save_error'))
                } finally {
                    if (!autoPaymentMode) {
                        this.loadingConfirm = false
                    }
                }

                if (autoPaymentMode) {
                    return {autoPaymentMode: true, invoiceId: silentPayInvoiceId}
                }
            }
        }

        async silentPayPrice(i) {
            this.issueItemBySupplier.prices = [this.issueItemBySupplier.prices[i]]
            await this.$nextTick(() => {})
            await this.postInvoices(true)
        }

        reset() {
            if (this.isAgency) {
                this.invoiceType = 'SUPPLIER'
            }
            if (this.$conf.public.account.paymentSchedulerAutoInvoiceRemaining) {
                this.autoInvoiceRemainingBalance = true
            }
            this.loadingConfirm = false
            this.loadingInfo = false
            this.description = null
        }

        removePrice(i) {
            this.issueItemBySupplier.prices.splice(i, 1)
        }

        get isShowTypeSwitcher() {
            if (this.$conf.public.account.paymentSchedulerAutoPaymentMode) {
                return this.isTO1 || this.isTO2
            } else {
                return this.isTO1
            }
        }

        get issueItemBySupplier() {
            if (!this.issueInfoItems?.length) {
                return null
            }

            return this.invoiceType === 'CUSTOMER'
                ? this.issueInfoItems[0]
                : this.issueInfoItems.find(i => i.supplierId === this.supplierId)
        }

        set issueItemBySupplier(val) {}

        get suppliers() {
            return this.issueInfoItems.map(i => ({supplierId: i.supplierId, supplierName: i.supplierName}))
        }

        get invoiceTypes() {
            return [
                {title: this.$t('supplier_payments'), value: 'SUPPLIER'},
                {title: this.$t('client_payments'), value: 'CUSTOMER'},
            ]
        }

        get isLoading() {
            return this.loadingConfirm || this.loadingInfo
        }

        get isMobile() {
            return this.$breakpoint.smAndDown
        }

        get isTO1() {
            return authStore.isTO1
        }

        get isTO2() {
            return authStore.isTO2
        }

        get isAgency() {
            return authStore.isAgency
        }

        get descriptionItems() {
            return this.invoiceType === 'CUSTOMER'
                ? this.$conf.public.account.paymentSchedulerDescriptionsClient?.map(t => this.$t(t))
                : this.$conf.public.account.paymentSchedulerDescriptionsSupplier?.map(t => this.$t(t))
        }
    }

    export default OrderAddDepositModal
</script>
